import * as React from 'react';
import {translate} from 'react-i18next';
import {connect} from 'react-redux';
import {Divider, Grid, Header, Segment} from 'semantic-ui-react';
import {
    SpecDto,
    ResourceListOfSpecDto,
    Link,
    SpecDtoSpecCategory,
    MenuDto,
} from '../../../services/Client';
import {ITranslatedComponent} from '../../../redux/reducers/generalReducer';
import Button from '../../../components/Button';
import {SpecsTable} from './SpecsTable';
import SpecCreationDialog from './SpecCreationDialog';
import _ from 'lodash';
import TextField from '../../../components/TextField';
import {renderErrorToast} from "../../../utils/FeedbackMessageUtils";
import {compose} from "redux";
import HATEOASClient from "../../../services/HATEOASClient";
import {selectAdminArealinks, selectClient} from '../../../redux/selectors';
import {IReduxState} from '../../../redux/IReduxState';

interface IMapStateToProps {
    adminAreaLinks?: MenuDto;
    client: HATEOASClient;
}

interface IOwnProps {

}

interface ConfigurationMaintenanceProps extends IOwnProps, IMapStateToProps, ITranslatedComponent {
}

interface ConfigurationMaintenanceState {
    /** Whether or not we are waiting for a response from the back end. */
    loadingSpecs: boolean;

    /** Abgas spec code filter. */
    filterAbgasSpecs: string | undefined;

    /** Abgas specs that match the current filter. */
    filteredAbgasSpecs?: SpecDto[];

    /** Rest spec name filter. */
    filterRestSpecs: string | undefined;

    /** Rest Specs that match the current filter. */
    filteredRestSpecs?: SpecDto[];

    /** Resource list of specs */
    specs?: ResourceListOfSpecDto;

    /** The availale specs categories */
    specCategories: String[];

    /** Whether or not the creation dialog is displayed */
    showSpecCreationDialog: boolean;

    /** Whether or not the edit mode is activated */
    editMode: boolean;

    /** Whether or not we can edit spec codes */
    canEdit: boolean;
}

class ConfigurationMaintenance extends React.Component<ConfigurationMaintenanceProps, ConfigurationMaintenanceState> {
    constructor(props: ConfigurationMaintenanceProps) {
        super(props);

        this.state = {
            loadingSpecs: false,
            filterAbgasSpecs: undefined,
            filterRestSpecs: undefined,
            showSpecCreationDialog: false,
            specCategories: [],
            editMode: false,
            canEdit: false
        };
    }

    componentDidMount() {
        this.requestSpecs();
    }

    requestSpecs = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_specs(this.props.adminAreaLinks).canExecute()) {
            renderErrorToast({
                message: "GLOBAL:MISSING_ACCESS_RIGHTS",
                timestamp: Date.now()
            });
            return;
        }

        this.setState({loadingSpecs: true});
        this.props.client.GET_specs(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfSpecDto) => {

                let filteredAbgasSpecs = _.filter(value.elements, item => item.specCategory === SpecDtoSpecCategory.ABGAS);
                let filteredRestSpecs = _.filter(value.elements, item => item.specCategory === SpecDtoSpecCategory.REST);

                if (this.state.filterAbgasSpecs !== undefined) {
                    // select only the specs that match the filter
                    filteredAbgasSpecs = this.applyFilter(filteredAbgasSpecs, this.state.filterAbgasSpecs);
                }

                if (this.state.filterRestSpecs !== undefined) {
                    // select only the specs that match the filter
                    filteredRestSpecs = this.applyFilter(filteredRestSpecs, this.state.filterRestSpecs);
                }

                const canEdit = _.find(value.elements, spec => this.props.client.PATCH_spec(spec).canExecute()) !== undefined ? true : false;

                // request was successfully fulfilled; reset loading state
                this.setState({loadingSpecs: false, filteredAbgasSpecs: filteredAbgasSpecs, filteredRestSpecs: filteredRestSpecs, specs: value, canEdit: canEdit});
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_SPECS",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingSpecs: false
                });
            });
    }

    applyFilter = (dtos: SpecDto[], filter: string): SpecDto[] => {
        const filterLowerCase = filter.toLowerCase();
        return dtos.filter(dto => _.includes(dto.specCode.toLowerCase(), filterLowerCase));
    }

    renderAbgasSpecsFilter = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_CODE")}
            value={this.state.filterAbgasSpecs}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                let filteredDtos = this.state.specs ? _.filter(this.state.specs.elements, item => item.specCategory === SpecDtoSpecCategory.ABGAS) : [];
                if (filterValue) {
                    filteredDtos = this.applyFilter(filteredDtos, filterValue);
                }
                this.setState({filterAbgasSpecs: filterValue, filteredAbgasSpecs: filteredDtos});
            }}
        />
    )

    renderRestSpecsFilter = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_CODE")}
            value={this.state.filterRestSpecs}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                let filteredDtos = this.state.specs ? _.filter(this.state.specs.elements, item => item.specCategory === SpecDtoSpecCategory.REST) : [];
                if (filterValue) {
                    filteredDtos = this.applyFilter(filteredDtos, filterValue);
                }
                this.setState({filterRestSpecs: filterValue, filteredRestSpecs: filteredDtos});
            }}
        />
    )

    renderRefreshBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("RELOAD")}
            labelPosition="after"
            icon={"refresh"}
            disabled={loading}
            onClick={() => {
                this.requestSpecs();
            }}
        />
    )

    renderAddSpecBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("ADD_SPEC")}
            labelPosition="after"
            icon={"add"}
            disabled={loading}
            onClick={() => {
                this.setState({showSpecCreationDialog: true});
            }}
        />
    )

    updateSpec = (oldSpec: SpecDto, newSpec: SpecDto): void => {
        this.setState({loadingSpecs: true});

        if (!this.props.client.PATCH_spec(oldSpec).canExecute()) {
            renderErrorToast({
                message: "GLOBAL:MISSING_ACCESS_RIGHTS",
                timestamp: Date.now()
            });
            return;
        }

        this.props.client.PATCH_spec(oldSpec).execute(newSpec)
            .then((value: Link) => {
                // spec was updated succesfully; refresh the redux store.
                // requestSpecs() takes care of resetting the loadingSpecs state.
                this.requestSpecs();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:UPDATE_SPEC",
                    translationOptions: {specCode: oldSpec.specCode},
                    details: reason,
                    timestamp: Date.now()
                });
                this.requestSpecs();
                this.setState({
                    loadingSpecs: false
                });
            });
    }

    deleteSpec = (spec: SpecDto): void => {
        this.setState({loadingSpecs: true});

        this.props.client.DELETE_spec(spec).execute(spec)
            .then((value: Link) => {
                // spec was deleted succesfully; refresh the redux store.
                // requestSpecs() takes care of resetting the loadingSpecs state.
                this.requestSpecs();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:DELETE_SPEC",
                    translationOptions: {specCode: spec.specCode},
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingSpecs: false
                });
            });
    }

    renderTable = (loading: boolean, specs?: SpecDto[]): JSX.Element => (
        <SpecsTable
            specs={specs}
            editMode={this.state.editMode}
            loading={loading}
            optionalControls={{
                canDeleteSpec: dto => this.props.client.DELETE_spec(dto).canExecute()
            }}
            onDeleteSpec={(dto: SpecDto) => (
                this.deleteSpec(dto)
            )}
            onUpdateSpec={(oldSpec: SpecDto, newSpec: SpecDto) => (
                this.updateSpec(oldSpec, newSpec)
            )}
        />
    )

    renderAddSpecDlg = (): JSX.Element => (
        <SpecCreationDialog
            onClose={(newSpec?: SpecDto) => {
                this.setState({showSpecCreationDialog: false});
                if (newSpec) {
                    this.addSpec(newSpec);
                }
            }}
        />
    )

    addSpec = (spec: SpecDto): void => {
        this.setState({loadingSpecs: true});

        if (this.state.specs) {
            this.props.client.POST_spec(this.state.specs).execute(spec)
                .then((value: Link) => {
                    this.requestSpecs();
                })
                .catch((reason: any) => {
                    window.scrollTo(0, 0);
                    renderErrorToast({
                        message: "ERRORS:CREATE_SPEC",
                        details: reason,
                        timestamp: Date.now()
                    });
                    this.setState({
                        loadingSpecs: false
                    });
                });
        }
    }

    public render() {

        return (
            <Segment>
                <div>
                    {
                        this.state.specs &&
                        this.props.client.POST_spec(this.state.specs).canExecute() &&
                        this.renderAddSpecBtn(this.state.loadingSpecs)
                    }
                    {this.state.canEdit &&
                        this.state.editMode ?
                        <Button
                            icon="close"
                            label={this.props.t("STOP_EDIT_SPECS")}
                            disabled={this.state.loadingSpecs}
                            onClick={() => {
                                this.setState({editMode: !this.state.editMode});
                            }}
                        />
                        :
                        this.state.canEdit &&
                        <Button
                            icon="pencil"
                            label={this.props.t("EDIT_SPECS")}
                            disabled={this.state.loadingSpecs}
                            onClick={() => {
                                this.setState({editMode: !this.state.editMode});
                            }}
                        />
                    }
                    {
                        this.renderRefreshBtn(this.state.loadingSpecs)}
                    {
                        this.state.showSpecCreationDialog &&
                        this.renderAddSpecDlg()
                    }
                </div>
                <br />
                <Segment>
                    <Grid columns={2} stackable={true} textAlign='center'>
                        <Divider vertical={true}>+</Divider>
                        <Grid.Row>
                            <Grid.Column>
                                <Header>
                                    {this.props.t("SPEC_ABGAS")}
                                </Header>
                                {this.renderAbgasSpecsFilter()}
                                {this.renderTable(this.state.loadingSpecs, this.state.filteredAbgasSpecs)}
                            </Grid.Column>
                            <Grid.Column>
                                <Header>
                                    {this.props.t("SPEC_REST")}
                                </Header>
                                {this.renderRestSpecsFilter()}
                                {this.renderTable(this.state.loadingSpecs, this.state.filteredRestSpecs)}
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            </Segment>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    adminAreaLinks: selectAdminArealinks(state),
    client: selectClient(state),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate(["SPECS", "GLOBAL"]),
    connect<IMapStateToProps>(mapStateToProps)
)(ConfigurationMaintenance);



// WEBPACK FOOTER //
// ./src/views/AdminView/ConfigMaintenanceTab/ConfigurationMaintenance.tsx